import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserCircle, CreditCard, Save, ArrowLeft, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { API, useAuth } from "../context/AuthContext";
import { PlanPicker, PLANS, useCheckout } from "../components/matrix/Paywall";

const display = "font-display";
const inputCls = "w-full bg-gray-950 border border-white/10 rounded-lg px-3 py-2 text-xs text-gray-100 focus:border-[#f59e0b]/50 focus:outline-none";

const PLAN_BADGE = {
  admin: "text-[#f59e0b] border-[#f59e0b]/40", unlimited: "text-[#2dd4bf] border-[#2dd4bf]/40",
  trial: "text-purple-300 border-purple-400/40", expired: "text-rose-400 border-rose-500/40",
};

export default function Account() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ full_name: "", phone_display: "", address: "", organization: "" });
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState("monthly");
  const [confirmDelete, setConfirmDelete] = useState("");
  const [deleting, setDeleting] = useState(false);
  const { loading, checkout } = useCheckout();

  const deleteAccount = async () => {
    if (confirmDelete !== "DELETE") return toast.error('Type DELETE to confirm.');
    setDeleting(true);
    try {
      await axios.delete(`${API}/account`);
      toast.success("Account deleted. Goodbye!");
      await logout();
      window.location.href = "/";
    } catch {
      toast.error("Couldn't delete your account. Try again.");
      setDeleting(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/account`).then((res) => {
      setData(res.data);
      const p = res.data.profile || {};
      setForm({
        full_name: p.full_name || res.data.name || "",
        phone_display: p.phone_display || "",
        address: p.address || "",
        organization: p.organization || "",
      });
    }).catch(() => toast.error("Couldn't load your account."));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/account/profile`, form);
      toast.success("Profile saved.");
    } catch {
      toast.error("Couldn't save profile.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0f1729]">
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="account-page">
        <Link to="/matrix" data-testid="account-back-link" className="inline-flex items-center gap-1.5 text-xs font-mono text-gray-400 hover:text-[#f59e0b] transition-colors duration-200">
          <ArrowLeft className="w-3.5 h-3.5" /> BACK TO MATRIX
        </Link>
        <h1 className={`${display} text-2xl font-bold text-white uppercase tracking-wide flex items-center gap-2`}>
          <UserCircle className="w-6 h-6 text-[#f59e0b]" /> Your Account
        </h1>

        {data && (
          <div className="bg-[#131f3a] border border-white/5 rounded-2xl p-6 space-y-3" data-testid="account-plan-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs text-gray-400 font-mono">{data.email}</div>
                <div className="text-[10px] text-gray-500 font-mono mt-0.5">Member since {String(data.created_at).slice(0, 10)}</div>
              </div>
              <span className={`text-[10px] font-mono font-bold uppercase border px-2.5 py-1 rounded-lg ${PLAN_BADGE[data.access.plan] || "text-emerald-400 border-emerald-500/40"}`} data-testid="account-plan-badge">
                {data.access.plan}{data.access.days_left != null ? ` · ${data.access.days_left}d left` : ""}
              </span>
            </div>
            {!["admin", "unlimited"].includes(data.access.plan) && (
              <div className="space-y-2 pt-2 border-t border-white/5">
                <p className="text-[11px] text-gray-400 flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Extend or upgrade your access — time stacks on top of what you have.</p>
                <PlanPicker selected={plan} onSelect={setPlan} />
                <button onClick={() => checkout(plan, form.full_name ? form : null)} disabled={loading} data-testid="account-checkout-btn" className={`w-full bg-[#f59e0b] hover:bg-[#fbbf24] disabled:opacity-60 text-gray-950 ${display} font-bold py-2.5 rounded-xl text-xs uppercase tracking-wider transition-colors duration-200`}>
                  {loading ? "..." : `Purchase ${PLANS.find((p) => p.id === plan).price}${PLANS.find((p) => p.id === plan).per}`}
                </button>
              </div>
            )}
          </div>
        )}

        <form onSubmit={save} className="bg-[#131f3a] border border-white/5 rounded-2xl p-6 space-y-3" data-testid="account-profile-form">
          <h2 className={`${display} text-sm font-bold text-white uppercase tracking-wide`}>Profile Details</h2>
          <p className="text-[11px] text-gray-400">Used on receipts, court-ready summaries, and collaboration invites.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] font-mono text-gray-400 uppercase">Full name</label>
              <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} maxLength={100} className={inputCls} data-testid="account-name-input" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-mono text-gray-400 uppercase">Phone</label>
              <input value={form.phone_display} onChange={(e) => setForm({ ...form, phone_display: e.target.value })} maxLength={30} className={inputCls} data-testid="account-phone-input" />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-mono text-gray-400 uppercase">Mailing address</label>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} maxLength={200} className={inputCls} data-testid="account-address-input" />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-mono text-gray-400 uppercase">Organization / role (optional)</label>
            <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} maxLength={100} placeholder="e.g., Self-represented, Smith Law LLP, Advocate" className={inputCls} data-testid="account-org-input" />
          </div>
          <button type="submit" disabled={saving} data-testid="account-save-btn" className="flex items-center gap-1.5 bg-[#2dd4bf] hover:bg-[#5eead4] text-gray-950 font-bold text-[10px] font-mono px-4 py-2 rounded-lg transition-colors duration-200 disabled:opacity-50">
            <Save className="w-3 h-3" /> {saving ? "SAVING..." : "SAVE PROFILE"}
          </button>
        </form>

        {data && data.transactions.length > 0 && (
          <div className="bg-[#131f3a] border border-white/5 rounded-2xl p-6 space-y-2" data-testid="account-transactions">
            <h2 className={`${display} text-sm font-bold text-white uppercase tracking-wide`}>Payment History</h2>
            {data.transactions.map((t, i) => (
              <div key={i} className="flex justify-between items-center text-[11px] font-mono border-b border-white/5 pb-1.5">
                <span className="text-gray-400">{String(t.created_at).slice(0, 10)} · {t.package_id}</span>
                <span className={t.payment_status === "paid" ? "text-emerald-400" : "text-gray-500"}>${t.amount} · {t.payment_status}</span>
              </div>
            ))}
          </div>
        )}

        <div className="bg-[#131f3a] border border-rose-500/20 rounded-2xl p-6 space-y-3" data-testid="account-danger-zone">
          <h2 className={`${display} text-sm font-bold text-rose-400 uppercase tracking-wide flex items-center gap-1.5`}>
            <Trash2 className="w-4 h-4" /> Danger Zone
          </h2>
          <p className="text-[11px] text-gray-400 leading-relaxed">
            Permanently delete your account and everything in it — all cases, events, uploaded files, insights, and shares. This cannot be undone.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={confirmDelete}
              onChange={(e) => setConfirmDelete(e.target.value)}
              placeholder='Type DELETE to confirm'
              className={`${inputCls} sm:max-w-[200px] border-rose-500/20 focus:border-rose-500/50`}
              data-testid="account-delete-confirm-input"
            />
            <button
              onClick={deleteAccount}
              disabled={deleting || confirmDelete !== "DELETE"}
              data-testid="account-delete-btn"
              className="flex items-center justify-center gap-1.5 bg-rose-600 hover:bg-rose-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-[10px] font-mono px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <Trash2 className="w-3 h-3" /> {deleting ? "DELETING..." : "DELETE MY ACCOUNT"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
